import React, { useState } from 'react';
import axios from 'axios';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  Avatar
} from '@mui/material';
import url from '../serverUrl';

const API_URL = url;

const CreateLeverageTokenForm = () => {
  const [formData, setFormData] = useState({
    symbol: '',
    name: '',
    description: '',
    logo: '',
    price: '',
    base_symbol: '',
    base_power: '',
    pair_symbol: ''
  });

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileForm = new FormData();
    fileForm.append('image', file);
    fileForm.append('fileName', file.name);

    setUploading(true);
    try {
      const res = await axios.post(
        `${API_URL}/hootdex/token-logo-upload`,
        fileForm
      );
      if (res.data.status === 'ok') {
        setFormData((prev) => ({
          ...prev,
          logo: res.data.fileName
        }));
        setAlert({ open: true, message: 'Logo uploaded', severity: 'success' });
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setAlert({
        open: true,
        message: 'Logo upload failed',
        severity: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAlert({ open: false, message: '', severity: 'success' });

    try {
      const payload = {
        ...formData,
        logo: formData.logo ? `${API_URL}/hootdex/images/${formData.logo}` : '',
        price: parseFloat(formData.price || 0),
        base_power: parseInt(formData.base_power || 1)
      };

      await axios.post(`${API_URL}/leverage`, payload);

      setAlert({
        open: true,
        message: 'Token created successfully',
        severity: 'success'
      });

      setFormData({
        symbol: '',
        name: '',
        description: '',
        logo: '',
        price: '',
        base_symbol: '',
        base_power: '',
        pair_symbol: ''
      });
    } catch (error) {
      setAlert({
        open: true,
        message: error.response?.data?.error || error.message,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ maxWidth: 600, mx: 'auto', mt: 4, p: 4 }}>
      <Typography variant="h5" gutterBottom>
        Create Leverage Token
      </Typography>

      {/* Logo Preview */}
      {formData.logo && (
        <Box display="flex" justifyContent="center" mt={2} mb={1}>
          <Avatar
            src={`${API_URL}/hootdex/images/${formData.logo}`}
            alt="Token Logo"
            sx={{ width: 72, height: 72 }}
          />
        </Box>
      )}

      {/* Logo Upload */}
      <Box display="flex" justifyContent="center" mt={1} mb={2}>
        <Button variant="outlined" component="label" disabled={uploading}>
          {uploading ? 'Uploading...' : 'Upload Logo'}
          <input
            hidden
            type="file"
            accept="image/*"
            onChange={handleLogoUpload}
          />
        </Button>
      </Box>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        {[
          { label: 'Symbol *', name: 'symbol' },
          { label: 'Name *', name: 'name' },
          { label: 'Description', name: 'description' },
          { label: 'Price (USD)', name: 'price' },
          { label: 'Base Symbol', name: 'base_symbol' },
          { label: 'Base Power', name: 'base_power' },
          { label: 'Pair Symbol', name: 'pair_symbol' }
        ].map(({ label, name }) => (
          <TextField
            key={name}
            fullWidth
            label={label}
            name={name}
            value={formData[name]}
            onChange={handleChange}
            variant="outlined"
            margin="normal"
          />
        ))}

        <Box mt={3} textAlign="right">
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading || uploading}
            startIcon={loading && <CircularProgress size={18} />}
          >
            {loading ? 'Creating...' : 'Create Token'}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={alert.severity}
          variant="filled"
          onClose={() => setAlert({ ...alert, open: false })}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default CreateLeverageTokenForm;
